import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  Pagination,
  Toolbar,
} from "@mui/material";
import FilterList from "@mui/icons-material/FilterList";
import Header from "../Components/header";
import Footer from "../Components/footer";
import { useAuth } from "../Context/authContext";
import ProjectFilters from "../Components/AllProjects/projectFilters";
import ProjectTable from "../Components/AllProjects/projectTable";
import theme from "../Styles/theme";
import { ThemeProvider } from "@mui/material/styles";
import {
  fetchAllProjects,
  fetchProjectManagers,
  exportProjects,
} from "../Controllers/projectController";
import { fetchUnits } from "../Controllers/unitController";
import { fetchActivities } from "../Controllers/activityController";
import { fetchStatuses } from "../Controllers/statusController";
import { fetchEmployees } from "../Controllers/employeeController";

function usePersistFiltersToStorage(storageKey, filters, setters) {
  const hydrated = useRef(false);

  // 1) Hydrate from storage once
  useEffect(() => {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      try {
        const s = JSON.parse(raw);
        if (s.showMyProjects !== undefined)
          setters.setShowMyProjects(s.showMyProjects);
        if (s.showMyUnitProjects !== undefined)
          setters.setShowMyUnitProjects(s.showMyUnitProjects);
        if (s.sortBy !== undefined) setters.setSortBy(s.sortBy);
        if (s.sortOrderActivity !== undefined)
          setters.setSortOrderActivity(s.sortOrderActivity);
        if (s.sortOrderStart !== undefined)
          setters.setSortOrderStart(s.sortOrderStart);
        if (s.sortOrderEnd !== undefined)
          setters.setSortOrderEnd(s.sortOrderEnd);
        if (s.limit !== undefined) setters.setLimit(s.limit);
        if (s.startDateFrom !== undefined)
          setters.setStartDateFrom(s.startDateFrom);
        if (s.endDateFrom !== undefined) setters.setEndDateFrom(s.endDateFrom);
        if (s.startDateTo !== undefined) setters.setStartDateTo(s.startDateTo);
        if (s.endDateTo !== undefined) setters.setEndDateTo(s.endDateTo);
        if (s.internal !== undefined) setters.setInternal(s.internal);
        if (s.searchFields !== undefined)
          setters.setSearchFields(s.searchFields);
        if (s.filterFields !== undefined)
          setters.setFilterFields(s.filterFields);
        if (s.showFilters !== undefined) setters.setShowFilters(s.showFilters);
        if (s.page !== undefined) setters.setPage(s.page);
      } catch {}
    }
    hydrated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Save whenever filters change (after hydration)
  useEffect(() => {
    if (!hydrated.current) return;
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [storageKey, filters]);
}

const AllProjects = () => {
  const { user } = useAuth();

  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [showMyProjects, setShowMyProjects] = useState(false);
  const [showMyUnitProjects, setShowMyUnitProjects] = useState(false);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrderActivity, setSortOrderActivity] = useState("DESC");
  const [sortOrderStart, setSortOrderStart] = useState("DESC");
  const [sortOrderEnd, setSortOrderEnd] = useState("DESC");
  const [limit, setLimit] = useState(25);
  const [startDateFrom, setStartDateFrom] = useState(null);
  const [endDateFrom, setEndDateFrom] = useState(null);
  const [startDateTo, setStartDateTo] = useState(null);
  const [endDateTo, setEndDateTo] = useState(null);
  const [internal, setInternal] = useState(false);
  const [searchFields, setSearchFields] = useState([]);
  const [filterFields, setFilterFields] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const filterProxy = (fn) => {
    fn();
    setPage(1);
  };

  usePersistFiltersToStorage(
    "project_filters",
    {
      showMyProjects,
      showMyUnitProjects,
      sortBy,
      sortOrderActivity,
      sortOrderStart,
      sortOrderEnd,
      limit,
      startDateFrom,
      endDateFrom,
      startDateTo,
      endDateTo,
      internal,
      searchFields,
      filterFields,
      showFilters,
      page,
    },
    {
      setShowMyProjects,
      setShowMyUnitProjects,
      setSortBy,
      setSortOrderActivity,
      setSortOrderStart,
      setSortOrderEnd,
      setLimit,
      setStartDateFrom,
      setEndDateFrom,
      setStartDateTo,
      setEndDateTo,
      setInternal,
      setSearchFields,
      setFilterFields,
      setShowFilters,
      setPage,
    },
  );

  const fetchDropdownData = async () => {
    try {
      const [unitRes, activityRes, statusRes, managersRes, employeesRes] =
        await Promise.all([
          fetchUnits(setUnits),
          fetchActivities(setActivities),
          fetchStatuses(setStatuses),
          fetchProjectManagers(setProjectManagers),
          fetchEmployees(setEmployees),
        ]);
      await Promise.all([
        unitRes,
        activityRes,
        statusRes,
        managersRes,
        employeesRes,
      ]);
    } catch (err) {
      console.error("Dropdown fetch error:", err);
    }
  };

  const handleExportClick = async () => {
    exportProjects(
      sortBy,
      sortOrderStart,
      sortOrderEnd,
      sortOrderActivity,
      showMyProjects,
      showMyUnitProjects,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      searchFields,
      filterFields,
    );
  };

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const clearAllFilters = () => {
    setInternal(false);
    setShowMyProjects(false);
    setShowMyUnitProjects(false);
    setStartDateFrom(null);
    setStartDateTo(null);
    setEndDateFrom(null);
    setEndDateTo(null);
    setSortBy("updatedAt");
    setSortOrderActivity("DESC");
    setSortOrderStart("DESC");
    setSortOrderEnd("DESC");
    setPage(1);
    setSearchFields([]);
    setFilterFields([]);
  };

  useEffect(() => {
    fetchAllProjects(
      sortBy,
      sortOrderStart,
      sortOrderEnd,
      sortOrderActivity,
      showMyProjects,
      showMyUnitProjects,
      limit,
      page,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      setProjects,
      setTotalPages,
      internal,
      searchFields,
      filterFields,
    );
  }, [
    showMyProjects,
    showMyUnitProjects,
    sortBy,
    sortOrderActivity,
    sortOrderStart,
    sortOrderEnd,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    limit,
    internal,
    searchFields,
    filterFields,
    page,
  ]);

  if (!user) {
    window.location.href = "/";
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <Header />
        <Toolbar sx={{ height: "120px" }} />
        <Box className="mainContainer">
          {/* {user.userData.userRoles.includes("Administrator") && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2 }}
              href="/dashboard"
            >
              Admin Dashboard
            </Button>
          )}
          {(user.userData.userRoles.includes("Administrator") ||
            user.userData.userRoles.includes("Accounting")) && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2 }}
              href="/accounting"
            >
              Accounting Dashboard
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 2 }}
            href="/project/new"
          >
            Open New Project
          </Button> */}

          {/* Filter Toggle Button for Mobile */}
          <Button
            variant="outlined"
            onClick={() => setShowFilters((prev) => !prev)}
            sx={{ display: "inline-flex", mb: 2 }}
            startIcon={<FilterList />}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>

          <ProjectFilters
            showFilters={showFilters}
            units={units}
            activities={activities}
            statuses={statuses}
            projectManagers={projectManagers}
            employees={employees}
            clearAllFilters={clearAllFilters}
            showMyProjects={showMyProjects}
            setShowMyProjects={(args) =>
              filterProxy(() => setShowMyProjects(args))
            }
            showMyUnitProjects={showMyUnitProjects}
            setShowMyUnitProjects={(args) =>
              filterProxy(() => setShowMyUnitProjects(args))
            }
            startDateFrom={startDateFrom}
            startDateTo={startDateTo}
            endDateFrom={endDateFrom}
            endDateTo={endDateTo}
            setStartDateFrom={(args) =>
              filterProxy(() => setStartDateFrom(args))
            }
            setStartDateTo={(args) => filterProxy(() => setStartDateTo(args))}
            setEndDateFrom={(args) => filterProxy(() => setEndDateFrom(args))}
            setEndDateTo={(args) => filterProxy(() => setEndDateTo(args))}
            internal={internal}
            setInternal={(args) => filterProxy(() => setInternal(args))}
            searchFields={searchFields}
            setSearchFields={(args) => filterProxy(() => setSearchFields(args))}
            filterFields={filterFields}
            setFilterFields={(args) => filterProxy(() => setFilterFields(args))}
          />

          <ProjectTable
            projects={projects}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrderActivity={sortOrderActivity}
            setSortOrderActivity={setSortOrderActivity}
            sortOrderStart={sortOrderStart}
            setSortOrderStart={setSortOrderStart}
            sortOrderEnd={sortOrderEnd}
            setSortOrderEnd={setSortOrderEnd}
          />

          {user.userData.userRoles.includes("Administrator") && (
            <Button
              variant="contained"
              color="primary"
              sx={{ my: 2, ml: 2 }}
              onClick={handleExportClick}
            >
              Export Projects List
            </Button>
          )}

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography>Rows per page:</Typography>
              <Select
                value={limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value, 10);
                  setLimit(newLimit);
                  setPage(1);
                  fetchAllProjects(
                    sortBy,
                    sortOrderStart,
                    sortOrderEnd,
                    sortOrderActivity,
                    showMyProjects,
                    showMyUnitProjects,
                    limit,
                    page,
                    startDateFrom,
                    startDateTo,
                    endDateFrom,
                    endDateTo,
                    setProjects,
                    setTotalPages,
                    internal,
                    searchFields,
                    filterFields,
                  );
                }}
                size="small"
              >
                {[5, 10, 25, 50].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </Box>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default AllProjects;
